import { Gap } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { MouseEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router';
import {
  navigateWithAuthTransition,
  shouldHandleTransitionLinkClick,
} from '../../auth.navigation';
import { authPath } from '../../model/auth.paths.model';
import {
  authVmCopies,
  type RegisterEmailStepValues,
  type RegisterStepFormState,
  type RegisterStepSharedProps,
} from '../../view-model/register.types';
import { AuthLayout } from '../components/AuthLayout';
import {
  AuthClientAppHint,
  AuthTermsNotice,
} from '../components/AuthFooterText';
import * as styles from '../register.view.css';
import {
  StepButton,
  StepForm,
  StepInput,
  StepTitle,
  toSignupFormId,
  toStepSubmitState,
  toSignupTestId,
} from './StepFormPrimitives';

const REGISTER_INLINE_INVALID_EMAIL_MESSAGE =
  'Please enter a valid email address.';

type EmailStepProps = {
  values: RegisterEmailStepValues;
  formState: RegisterStepFormState<'email'>;
  testimonialsContent: ReactNode;
} & Pick<
  RegisterStepSharedProps,
  'canContinue' | 'shouldAutoFocusInput' | 'dispatch'
>;

export const EmailStep = (p: EmailStepProps) => {
  const submitState = toStepSubmitState(p.formState);
  const signInPath = authPath('/login');
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const setValue = <TField extends keyof RegisterEmailStepValues>(
    field: TField,
    value: RegisterEmailStepValues[TField],
  ): void => {
    p.dispatch('setField', {
      step: 'email',
      field,
      value,
    });
  };

  const handleSignInClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!shouldHandleTransitionLinkClick(event)) {
      return;
    }

    event.preventDefault();
    navigateWithAuthTransition(signInPath);
  };

  const hasInvalidTouchedEmail =
    isEmailTouched &&
    p.values.email.trim().length > 0 &&
    !p.canContinue &&
    submitState.type !== 'error';
  const emailErrorMessage =
    submitState.type === 'error'
      ? submitState.message
      : hasInvalidTouchedEmail
      ? REGISTER_INLINE_INVALID_EMAIL_MESSAGE
      : null;
  const emailInputWrapperClassName = emailErrorMessage
    ? styles.authInputWrapperError
    : styles.authInputWrapper;

  return (
    <AuthLayout
      preset="register-two-column"
      rightColumn={p.testimonialsContent}
      content={
        <div className={styles.codeStepStack}>
          <StepTitle>
            <>Become a creator on Playbook</>
          </StepTitle>
          <Gap height="3xl" />
          <StepForm
            id={toSignupFormId('email')}
            step="email"
            onSubmit={() => {
              p.dispatch('continue');
            }}
          >
            <StepInput
              variant="email"
              autoFocus={p.shouldAutoFocusInput}
              placeholder={authVmCopies.common.emailAddressPlaceholder}
              value={p.values.email}
              testId={toSignupTestId('email', 'email', 'input')}
              error={emailErrorMessage ?? undefined}
              hideError
              inputWrapperClassName={emailInputWrapperClassName}
              onValueChange={(value) => {
                setIsEmailTouched(false);
                setValue('email', value);
              }}
              onBlur={() => setIsEmailTouched(true)}
              disabled={submitState.type === 'submitting'}
            />
            <Typography
              variant="body"
              size="small"
              className={styles.emailSupportingText}
            >
              This email will be used to log in. No password needed.
            </Typography>

            {emailErrorMessage ? (
              <Typography
                role="alert"
                variant="body"
                size="small"
                color="ERROR-100"
                className={styles.emailErrorText}
              >
                {emailErrorMessage}
              </Typography>
            ) : null}

            <Gap height="3xl" />
            <StepButton
              testId={toSignupTestId('email', 'create-account', 'button')}
              loading={submitState.type === 'submitting'}
              disabled={!p.canContinue || submitState.type === 'submitting'}
            >
              Create account
            </StepButton>
          </StepForm>
        </div>
      }
      footer={
        <div className={styles.entryFooter}>
          <AuthTermsNotice
            textClassName={styles.entryTosText}
            linkClassName={styles.entryTosLink}
          />

          <div className={styles.footerDivider} />

          <Typography
            variant="body"
            size="medium"
            className={styles.entryAccountText}
          >
            Already have an account?{' '}
            <Link
              to={signInPath}
              className={styles.entryAccountLink}
              data-testid={toSignupTestId('email', 'signin', 'link')}
              onClick={handleSignInClick}
            >
              Sign in
            </Link>
          </Typography>

          <AuthClientAppHint
            hintClassName={styles.bottomHint}
            linkClassName={styles.textLink}
          />
        </div>
      }
    />
  );
};
